import React, { useEffect, useState } from 'react';
import { Modal } from './Modal';
import { Button } from '../atoms';
import { FormField } from '../molecues/FormField';
import type { CastCreateRequest, CastUpdateRequest } from '../../types/api/cast';
import type { Cast } from '../../types/api/cast';

export interface CastFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * onSubmit receives a payload compatible with CastCreateRequest / CastUpdateRequest
   * and an optional profile image file when user chooses a new image.
   */
  onSubmit: (
    data: CastCreateRequest | CastUpdateRequest,
    profileImageFile?: File | null
  ) => void | Promise<void>;
  mode: 'create' | 'edit';
  cast?: Cast;
}

export const CastFormModal: React.FC<CastFormModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  mode,
  cast,
}) => {
  const [formData, setFormData] = useState<{
    name: string;
    birthDate: string;
    nationality: string;
    role: string;
  }>({
    name: '',
    birthDate: '',
    nationality: '',
    role: '',
  });

  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [profileImagePreview, setProfileImagePreview] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Populate form when editing
  useEffect(() => {
    if (cast && mode === 'edit') {
      setFormData({
        name: cast.name || '',
        birthDate: cast.birthDate ?? '',
        nationality: cast.nationality ?? '',
        role: cast.role ?? '',
      });
      setProfileImagePreview(cast.profileImageUrl || null);
      setProfileImageFile(null);
    } else if (isOpen) {
      setFormData({
        name: '',
        birthDate: '',
        nationality: '',
        role: '',
      });
      setProfileImagePreview(null);
      setProfileImageFile(null);
    }
    setErrors({});
  }, [cast, mode, isOpen]);

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2 || formData.name.trim().length > 100) {
      newErrors.name = 'Name must be between 2 and 100 characters';
    }

    if (formData.nationality && formData.nationality.length > 50) {
      newErrors.nationality = 'Nationality must be at most 50 characters';
    }

    if (formData.role && formData.role.length > 50) {
      newErrors.role = 'Role must be at most 50 characters';
    }

    if (formData.birthDate) {
      const inputDate = new Date(formData.birthDate);
      const now = new Date();
      if (Number.isNaN(inputDate.getTime())) {
        newErrors.birthDate = 'Invalid date';
      } else if (inputDate > now) {
        newErrors.birthDate = 'Birth date cannot be in the future';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleProfileImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setErrors((prev) => ({
        ...prev,
        profileImage: 'Please select a valid image file',
      }));
      return;
    }

    const maxSizeMb = 5;
    if (file.size > maxSizeMb * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        profileImage: `Image size must be less than ${maxSizeMb}MB`,
      }));
      return;
    }

    setProfileImageFile(file);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.profileImage;
      return next;
    });

    const reader = new FileReader();
    reader.onload = () => {
      setProfileImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: CastCreateRequest | CastUpdateRequest = {
        name: formData.name.trim(),
        birthDate: formData.birthDate || undefined,
        nationality: formData.nationality || undefined,
        role: formData.role || undefined,
      };

      await onSubmit(payload, profileImageFile);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={mode === 'create' ? 'Add New Cast' : 'Edit Cast'}
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            isLoading={isSubmitting}
            type="submit"
          >
            {mode === 'create' ? 'Create' : 'Update'}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Name"
            value={formData.name}
            onChange={(e) => handleChange('name', e.target.value)}
            error={errors.name}
            required
            placeholder="e.g., Song Kang"
          />

          <FormField
            label="Birth Date"
            type="date"
            value={formData.birthDate}
            onChange={(e) => handleChange('birthDate', e.target.value)}
            error={errors.birthDate}
            placeholder="YYYY-MM-DD"
          />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <FormField
            label="Nationality"
            value={formData.nationality}
            onChange={(e) => handleChange('nationality', e.target.value)}
            error={errors.nationality}
            placeholder="e.g., Korean"
          />

          <FormField
            label="Profession / Role"
            value={formData.role}
            onChange={(e) => handleChange('role', e.target.value)}
            error={errors.role}
            placeholder="e.g., Actor, Director"
            helperText="Global profession label for this cast member"
          />
        </div>

        <div className="w-full">
          <label className="block text-sm font-medium text-left text-gray-700 mb-1.5">
            Profile Image
          </label>
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden">
              {profileImagePreview ? (
                <img
                  src={profileImagePreview}
                  alt={formData.name || 'Profile preview'}
                  className="w-full h-full object-cover"
                />
              ) : (
                <span className="text-xs text-gray-400">No image</span>
              )}
            </div>
            <div>
              <input
                type="file"
                accept="image/*"
                onChange={handleProfileImageChange}
                className="block text-sm text-gray-700"
              />
              <p className="mt-1 text-xs text-gray-500">
                JPEG, PNG or WebP. Max size 5MB.
              </p>
              {errors.profileImage && (
                <p className="text-sm text-red-600 mt-1.5">{errors.profileImage}</p>
              )}
            </div>
          </div>
        </div>
      </form>
    </Modal>
  );
};


